// src/features/executive/account/widgets/FinancialSummaryCard.tsx
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import type { ExecutiveState } from 'app/slice/executive.slice'
import { Bar } from 'react-chartjs-2'
import {
  BarElement,
  BarController,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from 'chart.js'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import { useFinancialSummary } from '../hooks/use-financial-summary'
import { formatCurrency, formatDateThaiShort, formatVariance } from 'utils/executive.utils'

ChartJS.register(
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  ChartTooltip,
  Legend
)

// When the user toggles every legend series off, chart.js keeps drawing
// empty axes and auto-scales the y-axis to 0…1 (which formatCurrency
// rounds into a duplicate ฿0/฿1 ladder). Two-part fix:
//   1. beforeLayout: hide the y-axis so those bogus ticks disappear.
//   2. afterDraw: paint a clear message onto the plot area pointing the
//      user back at the legend — the chart (and its legend) stays mounted
//      so a single click restores whichever series they want.
const legendHiddenOverlayPlugin = {
  id: 'financialSummaryLegendHiddenOverlay',
  beforeLayout(chart: any) {
    const anyVisible = chart.data.datasets.some((_: any, i: number) => chart.isDatasetVisible(i))
    if (chart.options.scales?.y) {
      chart.options.scales.y.display = anyVisible
    }
  },
  afterDraw(chart: any) {
    const anyVisible = chart.data.datasets.some((_: any, i: number) => chart.isDatasetVisible(i))
    if (anyVisible) return
    const { ctx, chartArea } = chart
    if (!chartArea) return
    ctx.save()
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = '#616161'
    const cx = (chartArea.left + chartArea.right) / 2
    const cy = (chartArea.top + chartArea.bottom) / 2
    ctx.font = '600 15px "Sarabun", sans-serif'
    ctx.fillText('ชุดข้อมูลถูกซ่อนทั้งหมด', cx, cy - 10)
    ctx.font = '400 13px "Sarabun", sans-serif'
    ctx.fillStyle = '#9e9e9e'
    ctx.fillText('คลิกที่ป้ายชื่อด้านล่างเพื่อแสดงข้อมูลอีกครั้ง', cx, cy + 12)
    ctx.restore()
  },
}

interface KpiProps {
  label: string
  value: number
  previous: number | null
  positiveIsGood: boolean
  tooltip?: string
}
const KpiCell = ({ label, value, previous, positiveIsGood, tooltip }: KpiProps) => {
  const variance =
    previous != null && previous !== 0 ? (value - previous) / Math.abs(previous) : null
  const good = variance != null && (positiveIsGood ? variance >= 0 : variance <= 0)
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        {tooltip && (
          <Tooltip
            title={
              <Box sx={{ whiteSpace: 'pre-line', fontSize: 12, lineHeight: 1.5 }}>
                {tooltip}
              </Box>
            }
            arrow
            placement="top"
          >
            <InfoOutlinedIcon
              sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }}
            />
          </Tooltip>
        )}
      </Stack>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>
        {formatCurrency(value)}
      </Typography>
      <Typography variant="caption" color={good ? 'success.main' : 'error.main'}>
        {formatVariance(variance)}
      </Typography>
    </Box>
  )
}

// Account figures are clinic-level bookkeeping; these filters narrow the
// neighbouring performance / sales widgets but not this card (UR-2609-1158).
const IGNORED_FILTER_LABELS: Array<[keyof ExecutiveState['filters'], string]> = [
  ['doctorId', 'DOCTOR'],
  ['procedureId', 'PROCEDURE'],
  ['patientType', 'PATIENT_TYPE'],
]

export default function FinancialSummaryCard() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useFinancialSummary()
  const filters = useSelector((s: { executive: ExecutiveState }) => s.executive.filters)
  const ignored = IGNORED_FILTER_LABELS
    .filter(([k]) => filters[k] != null)
    .map(([, label]) => t(`EXECUTIVE.FILTER.${label}`))
  const subtitle = ignored.length > 0
    ? `${t('EXECUTIVE.ACCOUNT.FIN_SUMMARY.SUBTITLE')} · ${t('EXECUTIVE.FILTER.CLINIC_WIDE_NOTE', { filters: ignored.join(' / ') })}`
    : t('EXECUTIVE.ACCOUNT.FIN_SUMMARY.SUBTITLE')

  // Also treat "every bucket sub-baht on all three series" as empty —
  // otherwise chart.js auto-scales a flat-zero dataset and renders duplicate
  // ticks (฿0, ฿0, ฿0, ฿1, ฿1, ฿1) rounded from 0…1 instead of the empty
  // state. `net` can be negative (loss), so compare on absolute value.
  const isEmpty = !data
    || data.points.length === 0
    || data.points.every((p) =>
      (!p.income || p.income < 1)
      && (!p.expense || p.expense < 1)
      && (p.net == null || Math.abs(p.net) < 1)
    )

  const chartData = useMemo(() => {
    if (!data) return null
    return {
      labels: data.points.map((p) => formatDateThaiShort(p.bucket)),
      datasets: [
        {
          type: 'bar' as const,
          label: t('EXECUTIVE.ACCOUNT.FIN_SUMMARY.INCOME'),
          data: data.points.map((p) => p.income),
          backgroundColor: '#2a8d9c',
        },
        {
          type: 'bar' as const,
          label: t('EXECUTIVE.ACCOUNT.FIN_SUMMARY.EXPENSE'),
          data: data.points.map((p) => p.expense),
          backgroundColor: '#e67e22',
        },
        {
          type: 'line' as const,
          label: t('EXECUTIVE.ACCOUNT.FIN_SUMMARY.NET'),
          data: data.points.map((p) => p.net),
          borderColor: '#4caf50',
          backgroundColor: '#4caf50',
          pointRadius: 3,
          fill: false,
          tension: 0.2,
        },
      ],
    }
  }, [data, t])

  return (
    <WidgetCard
      title={t('EXECUTIVE.ACCOUNT.FIN_SUMMARY.TITLE')}
      titleTooltip={t('EXECUTIVE.ACCOUNT.FIN_SUMMARY.TOOLTIP')}
      subtitle={subtitle}
      loading={loading}
      error={error}
      empty={isEmpty}
      onRetry={refetch}
      skeletonHeight={380}
    >
      {data && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 2 }}>
            <KpiCell
              label={t('EXECUTIVE.ACCOUNT.FIN_SUMMARY.INCOME')}
              tooltip={t('EXECUTIVE.ACCOUNT.FIN_SUMMARY.INCOME_TOOLTIP')}
              value={data.income}
              previous={data.previousIncome}
              positiveIsGood
            />
            <KpiCell
              label={t('EXECUTIVE.ACCOUNT.FIN_SUMMARY.EXPENSE')}
              tooltip={t('EXECUTIVE.ACCOUNT.FIN_SUMMARY.EXPENSE_TOOLTIP')}
              value={data.expense}
              previous={data.previousExpense}
              positiveIsGood={false}
            />
            <KpiCell
              label={t('EXECUTIVE.ACCOUNT.FIN_SUMMARY.NET')}
              tooltip={t('EXECUTIVE.ACCOUNT.FIN_SUMMARY.NET_TOOLTIP')}
              value={data.net}
              previous={data.previousNet}
              positiveIsGood
            />
          </Stack>
          <Box sx={{ height: 280 }}>
            <Bar
              data={chartData as any}
              plugins={[legendHiddenOverlayPlugin]}
              options={{
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                  legend: { position: 'bottom' },
                  tooltip: {
                    callbacks: {
                      label: (ctx) =>
                        `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`,
                    },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: { callback: (v) => formatCurrency(Number(v)) },
                  },
                },
              }}
            />
          </Box>
        </>
      )}
    </WidgetCard>
  )
}
